import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react'
import DAIModal from 'src/DAIComponents/Modal/DAIModal'

type ModalOptions = {
  maxWidth?:
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  showCloseButton?: boolean
}

// `T` is a generic type representing any kind of value or viewModel
type ModalContextType<T = unknown> = {
  openModal: (content: ReactNode, options?: ModalOptions, value?: T) => void
  closeModal: () => void
  value: T | null
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export function useModal<T>() {
  const context = useContext(ModalContext as React.Context<ModalContextType<T>>)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

export const ModalProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false)
  const [content, setContent] = useState<ReactNode>(null)
  const [options, setOptions] = useState<ModalOptions>({})
  const [value, setValue] = useState<unknown>(null)

  const openModal = useCallback(
    (modalContent: ReactNode, opts?: ModalOptions, val?: unknown) => {
      setContent(modalContent)
      setOptions(opts || {})
      setValue(val ?? null)
      setIsOpen(true)
    },
    []
  )

  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <ModalContext.Provider value={{ openModal, closeModal, value }}>
      {children}
      <DAIModal
        isOpen={isOpen}
        onClose={closeModal}
        maxWidth={options.maxWidth}
        showCloseButton={options.showCloseButton ?? true}
      >
        {content}
      </DAIModal>
    </ModalContext.Provider>
  )
}
